/**
 * An OGraf graphic version's package files (0.93.0), fetched with the session for the sandboxed preview frame.
 *
 * The app's origin serves package files only as application/octet-stream downloads (uploaded JavaScript must never
 * run as script from PRISM's origin); their real types come with them in X-Prism-Content-Type and in the listing.
 * The bytes are posted into the opaque-origin preview frame, which makes its own blob: URLs from them. Versions
 * never change, so a package is fetched once per page.
 */
import { useQueries, useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

export interface OgrafPackageData {
  /** `<template id>@<version>`: the frame keeps one set of blob URLs per key. */
  key: string;
  main: string;
  files: Record<string, { data: ArrayBuffer; mime: string }>;
}

interface Listing {
  main: string;
  checksumSha256: string;
  files: Array<{ path: string; size: number; mime: string; sha256: string; url: string | null }>;
}

const path = (p: string) => p.split('/').map(encodeURIComponent).join('/');

async function fetchPackage(id: string, version: number): Promise<OgrafPackageData> {
  const base = `/api/v1/graphics/templates/${encodeURIComponent(id)}/${version}/files`;
  const listing = await api<Listing>(base);
  const files: OgrafPackageData['files'] = {};
  await Promise.all(listing.files.map(async (f) => {
    const res = await fetch(`${base}/${path(f.path)}`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`The graphic's file ${f.path} could not be loaded (${res.status})`);
    files[f.path] = { data: await res.arrayBuffer(), mime: res.headers.get('x-prism-content-type') ?? f.mime };
  }));
  return { key: `${id}@${version}`, main: listing.main, files };
}

const options = (id: string | undefined, version: number | undefined, enabled = true) => ({
  queryKey: ['ograf-package', id, version] as const,
  queryFn: () => fetchPackage(id!, version!),
  enabled: Boolean(id && version) && enabled,
  staleTime: Infinity,
  gcTime: 10 * 60_000,
  retry: 1,
});

export function useOgrafPackage(id: string | undefined, version: number | undefined, enabled = true) {
  return useQuery(options(id, version, enabled));
}

/** Several packages at once (the sequence viewer: every OGraf graphic on screen or about to be). */
export function useOgrafPackages(keys: Array<{ id: string; version: number }>) {
  return useQueries({ queries: keys.map((k) => options(k.id, k.version)) });
}

/** blob: URLs on PRISM's own origin for showing package images in the app (the image picker's thumbnails). */
export function imageUrl(pkg: OgrafPackageData | undefined, file: string, cache: Map<string, string>): string | null {
  const f = pkg?.files[file];
  if (!f || !/^image\/(png|jpeg|webp|gif|avif)$/.test(f.mime)) return null;
  const key = `${pkg!.key}|${file}`;
  let url = cache.get(key);
  if (!url) {
    url = URL.createObjectURL(new Blob([f.data], { type: f.mime }));
    cache.set(key, url);
  }
  return url;
}
